import React, { useEffect, useState } from "react";
import { Award, Loader2, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/utils/utils";
import { LeaderboardCardData } from "../utils/buildLeaderboards";
import { certificateKey } from "../certificates/certificateData";

/** Identity for a leaderboard member: name plus employing company. */
export interface LeaderboardMember {
  fullName: string;
  company: string;
}

const rankBadgeClass = (rank: number): string => {
  switch (rank) {
    case 1:
      return "bg-amber-400 text-amber-950";
    case 2:
      return "bg-slate-300 text-slate-800";
    case 3:
      return "bg-orange-300 text-orange-950";
    default:
      return "bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-300";
  }
};

interface LeaderboardCardProps {
  card: LeaderboardCardData;
  monthLabel: string;
  selectedKeys: ReadonlySet<string>;
  /** Generates and downloads one certificate; the page owns the assembly. */
  onDownloadCertificate: (category: string, fullName: string) => Promise<void>;
  onToggleSelect: (category: string, fullName: string, company: string) => void;
  onToggleCardAll: (
    category: string,
    members: LeaderboardMember[],
    select: boolean,
  ) => void;
}

const LeaderboardCard: React.FC<LeaderboardCardProps> = ({
  card,
  monthLabel,
  selectedKeys,
  onDownloadCertificate,
  onToggleSelect,
  onToggleCardAll,
}) => {
  // In-flight and failed downloads are tracked per certificate key, not with a
  // single busy/error slot: two members can be downloaded at once (or in quick
  // succession), and a shared slot would let the second click clobber the
  // first's spinner/error UI and re-enable an already-running download.
  const [busyKeys, setBusyKeys] = useState<ReadonlySet<string>>(new Set());
  const [errorKeys, setErrorKeys] = useState<ReadonlySet<string>>(new Set());

  // Busy/error markers are keyed by certificateKey, which omits the month; the
  // parent reconciles cards by `key={card.category}`, so on a re-run for a
  // DIFFERENT month this same instance persists and would otherwise keep the
  // prior month's spinner/failure markers. Clear them whenever the month
  // changes so markers never bleed across months. (An in-flight download's
  // `finally` deletes an already-absent key via functional setState — harmless.)
  useEffect(() => {
    setBusyKeys(new Set());
    setErrorKeys(new Set());
  }, [monthLabel]);

  const members: LeaderboardMember[] = card.entries.map((entry) => ({
    fullName: entry.fullName,
    company: entry.company,
  }));
  const entryKeys = card.entries.map((entry) =>
    certificateKey(card.category, entry.fullName, entry.company),
  );
  // Two members can collapse to the same certificateKey (same name + company in
  // one category); dedupe before counting so the tri-state header checkbox
  // isn't skewed by a key counted twice.
  const uniqueKeys = [...new Set(entryKeys)];
  const selectedCount = uniqueKeys.filter((key) =>
    selectedKeys.has(key),
  ).length;
  const allSelected = selectedCount > 0 && selectedCount === uniqueKeys.length;
  const headerChecked: boolean | "indeterminate" = allSelected
    ? true
    : selectedCount > 0
      ? "indeterminate"
      : false;

  const withoutKey = (
    set: ReadonlySet<string>,
    key: string,
  ): ReadonlySet<string> => {
    const next = new Set(set);
    next.delete(key);
    return next;
  };
  const withKey = (
    set: ReadonlySet<string>,
    key: string,
  ): ReadonlySet<string> => new Set(set).add(key);

  const handleCertificate = async (fullName: string, key: string) => {
    setBusyKeys((prev) => withKey(prev, key));
    setErrorKeys((prev) => withoutKey(prev, key));
    try {
      await onDownloadCertificate(card.category, fullName);
    } catch {
      setErrorKeys((prev) => withKey(prev, key));
    } finally {
      setBusyKeys((prev) => withoutKey(prev, key));
    }
  };

  return (
    <Card className="flex flex-col">
      <CardHeader className="gap-1 border-b border-gray-100 p-4 pb-3 dark:border-slate-700">
        <div className="flex items-start gap-2.5">
          <Checkbox
            aria-label={`Select all shown members in ${card.category}`}
            checked={headerChecked}
            // The partial ("indeterminate") state needs its own fill; without it
            // the default faded background renders a white glyph on near-white.
            className="mt-0.5 data-[state=indeterminate]:border-blue-600 data-[state=indeterminate]:bg-blue-600"
            disabled={card.entries.length === 0 || !monthLabel}
            icon={headerChecked === "indeterminate" ? <Minus /> : undefined}
            onCheckedChange={() =>
              onToggleCardAll(card.category, members, !allSelected)
            }
            size="sm"
          />
          <div className="min-w-0 flex-1">
            <CardTitle className="text-sm leading-snug">
              {card.category}
            </CardTitle>
            <div className="mt-1 flex items-center justify-between text-xs text-gray-500 dark:text-slate-400">
              <span className="truncate">{card.valueLabel}</span>
              <span className="flex-shrink-0 pl-2">
                Top {card.entries.length} of{" "}
                {card.totalMembers.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {card.entries.length === 0 ? (
          <div className="p-4 text-sm text-gray-400 dark:text-slate-500">
            No members.
          </div>
        ) : (
          <ol className="divide-y divide-gray-100 dark:divide-slate-700">
            {card.entries.map((entry, index) => {
              // The certificate key (deduped for the header count) is the single
              // identity used for busy/error/selection, so a row's spinner,
              // failure marker, and checkbox all agree even when two rows
              // collapse to the same key. The list-render key still carries the
              // rank so React sees each <li> as distinct.
              const certKey = entryKeys[index];
              const rowKey = `${entry.rank}-${certKey}`;
              const busy = busyKeys.has(certKey);
              const failed = errorKeys.has(certKey);
              const selected = selectedKeys.has(certKey);
              return (
                <li
                  className="group flex items-center gap-2.5 px-4 py-2"
                  key={rowKey}
                >
                  <Checkbox
                    aria-label={`Select ${entry.fullName} (${entry.company})`}
                    checked={selected}
                    disabled={!monthLabel}
                    onCheckedChange={() =>
                      onToggleSelect(
                        card.category,
                        entry.fullName,
                        entry.company,
                      )
                    }
                    size="sm"
                  />
                  <span
                    className={cn(
                      "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums",
                      rankBadgeClass(entry.rank),
                    )}
                  >
                    {entry.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                      {entry.fullName}
                    </div>
                    <div className="truncate text-xs text-gray-500 dark:text-slate-400">
                      {entry.company}
                      {entry.cityState ? ` · ${entry.cityState}` : ""}
                    </div>
                  </div>
                  <span className="flex-shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                    {entry.valueDisplay}
                  </span>
                  <button
                    aria-label={`Download certificate for ${entry.fullName}`}
                    className={cn(
                      "flex h-6 w-6 flex-shrink-0 items-center justify-center rounded transition-opacity hover:text-primary focus:opacity-100 focus:outline-none",
                      failed
                        ? "text-red-500"
                        : "text-gray-400 dark:text-slate-500",
                      // Pointer devices get the hover-reveal; a busy/failed cert
                      // stays visible regardless. On touch / no-hover devices
                      // there's no hover to reveal it, so force it always-visible
                      // there — otherwise the primary action is unreachable.
                      busy || failed
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
                    )}
                    disabled={busy || !monthLabel}
                    onClick={() => handleCertificate(entry.fullName, certKey)}
                    title={
                      failed
                        ? "Certificate failed — click to retry"
                        : "Download certificate"
                    }
                    type="button"
                  >
                    {busy ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Award className="h-3.5 w-3.5" />
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
};

export default LeaderboardCard;
